import React, { useEffect, useState, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import {
	Modal,
	Button,
	SocialPreview,
	ErrorBanner
} from "rhombus-react";
import { push } from "connected-react-router";
import { reset } from "redux-form";

import useActiveCreative from "hooks/useActiveCreative";
import useCreativeDetail from "hooks/useCreativeDetail";

import {
	loadFacebookPreview,
	resetPlacement,
	loadPACTypes,
	changePlacement,
	changeTab
} from "reducers/Social";
import { facebookPreviewSelector } from "selectors/Social2";
import { getFacebookDSPConnectionId } from "selectors/Social";

import {
	getPlacementByType,
	// isAdstudioCreative,
	buildAdStudioIframeUrl,
	iframeDimsRegex,
	scaleIframe,
	getManifest
} from "utils/common";
// import { isApproved } from "utils/CreativeActions";
import {
	PREVIEW_2_URL,
	API_URL,
	SOCIAL_AD_TYPES,
	SOCIAL_PLATFORM_TYPES,
	ADSTUDIO_IFRAME_ASSET_DIMENSIONS
} from "utils/constants";
import { LABEL_MANAGEMENT } from "widgets/forms/list";

import ProgressContainer from "widgets/ProgressContainer";
import CreativePreviewBladeToolbar from "widgets/modals/creatives/preview/CreativePreviewBladeToolbar";
import LabelsPanel from "widgets/modals/creatives/preview/LabelsPanel";
import { getFlatSocialVals } from "widgets/social2/utils";

import FacebookSocialTabSelector from "./FacebookSocialTabSelector";
import FacebookMOSocialTabSelector from "./FacebookMOSocialTabSelector";
import GooglePreview from "./GooglePreview";
import { FACEBOOK_TAB } from "./Tabs";

import "./SocialPreviewBlade.css";

const fbTypes = [
	SOCIAL_AD_TYPES.FACEBOOK_IMAGE,
	SOCIAL_AD_TYPES.FACEBOOK_VIDEO,
	SOCIAL_AD_TYPES.FACEBOOK_IMAGE_MO,
	SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO,
	SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL,
	SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO,
];

const fbMOTypes = [
	SOCIAL_AD_TYPES.FACEBOOK_IMAGE_MO,
	SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO,
	SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO
];

const googleTypes = [
	SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_CAROUSEL,
	SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_IMAGE,
];

function useClickOutsideHandler(onClickOutside){
	let modalRef = useRef();

	function handler(e){
		let node = ReactDOM.findDOMNode(modalRef.current);
		if(node){
			if(!node.contains(e.target)){
				onClickOutside();
			}
		}
	}

	useEffect(() => {
		document.addEventListener("mousedown", handler, true);

		return () => {
			document.removeEventListener("mousedown", handler, true);
		};
	}, []);

	return [modalRef];
}


export default function PreviewBlade(props){
	let { returnPath, libraryId, creativeId } = props;
	let [modalRef] = useClickOutsideHandler(closeModal);
	let dispatch = useDispatch();
	useActiveCreative(creativeId);

	useEffect(()=>{
		dispatch(loadPACTypes());
	}, []);

	useCreativeDetail(creativeId);

	let [isFullScreen, setIsFullScreen] = useState(false)
		, [isMuted, setIsMuted] = useState(true)
		, [reloadCount, setReloadCount] = useState(0)
		, {
			isLoaded,
			isMetaDataLoaded,
			adType,
			socialValues,
			filename,
			format,
			dspConnectionId,
			ftPreviewSrc,
			ftPreviewSrcError,
			pacTypes,
			selectedTab,
			aspectRatio,
			placement,
			// creativeDetails,
		} = useSelector((state) => {
			return{
				isLoaded: state.creativeDetails?.isLoaded,
				isMetaDataLoaded: state.social.metadata?.isLoaded,
				adType: state.creativeDetails?.adType,
				socialValues: state.creativeDetails?.socialValues || {},
				filename: state.creativeDetails?.filename,
				format: state.social.selectedPlacement.value,
				dspConnectionId:  getFacebookDSPConnectionId(state),
				ftPreviewSrc: (state.social.preview._html?.url.replace(/&amp;/gi, "&") || ""),
				ftPreviewSrcError: state.social.preview?.error?.description,
				pacTypes: state.social.pacTypes,
				selectedTab: state.social.selectedTab.value,
				aspectRatio: state.social.selectedAspectRatio,
				placement: state.social.facebookPlacementType.value,
				// creativeDetails: state.creativeDetails,
			};
		});

	let flattenedSocialVals = useMemo(() => {
		return getFlatSocialVals(socialValues);
	}, [socialValues]);

	const isCarouselVideo = useMemo(() => {
		if (adType !== SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO) {
			return false;
		}

		const cards = flattenedSocialVals?.cards || [];
		return cards.some(card => card.type === "video");
	}, [flattenedSocialVals, adType]);

	let selectedPAC = useMemo(() => {
		const pacType = socialValues?.placementAssetCustomizationType;
		const isPAC = pacType && (pacType !== "none");
		if(!isPAC) { return; }
		return getPlacementByType(socialValues?.placements, placement);
	}, [socialValues, placement]);

	// const isAdStudio = isAdstudioCreative(creativeDetails);
	const [iframeDimensions, setiframeDimensions] = useState({});

	let adStudioIframe
		, adStudioIframeProps
		, shouldRenderIframe = false
		;

	// HOPI-3062 - In the Creative View, we should *always* render the socialValues assets
	// if(isAdStudio){
	// 	if(creativeDetails.isDynamic){
	// 		if(!isApproved(creativeDetails.approval)){
	// 			shouldRenderIframe = true;
	// 		}
	// 	}
	// }


	useEffect(() => {
		if(!shouldRenderIframe) { return; }

		dispatch(getManifest(libraryId, creativeId))
			.then((resp) => {
				if(resp?.success) {
					let parsedResponse = resp.payload.data.match(iframeDimsRegex);
					let [width, height] = parsedResponse;

					let widthVal = width.replace(/"width":/, "").trim();
					widthVal = parseInt(widthVal);

					let heightVal = height.replace(/"height":/, "").trim();
					heightVal = parseInt(heightVal);

					setiframeDimensions({ iframeWidth: widthVal, iframeHeight: heightVal });
				}
			});
	}, [shouldRenderIframe, libraryId, creativeId]);

	if(shouldRenderIframe) {
		adStudioIframeProps = {
			src: buildAdStudioIframeUrl(libraryId, creativeId),
			frameProps: JSON.stringify({
				adPreview: true,
				ftURLMap: {
					videoBaseURL: "//cdn.flashtalking.com",
				},
			}),
		};
	}

	if(adStudioIframeProps) {
		const { width } = ADSTUDIO_IFRAME_ASSET_DIMENSIONS[adType] || {};
		const { iframeWidth, iframeHeight } = iframeDimensions;

		const calculateScale = scaleIframe(width, iframeWidth);
		const style = {
			width: iframeWidth,
			height: iframeHeight,
			transform: `scale(${calculateScale})`,
			transformOrigin: "top left",
		};

		adStudioIframe = (
			<iframe
				className="ad-studio-iframe"
				src={adStudioIframeProps.src}
				scrolling="no"
				name={adStudioIframeProps.frameProps}
				style={style}
			/>
		);
	}

	// , adTypeClass = adType?.toLowerCase().split("youtube").join("youtube-") || ""
	let headerProps = { icon:adType }
		, isTypeFBMO = fbMOTypes.includes(adType)
		// eslint-disable-next-line no-magic-numbers
		, fbPreviewVersion = isTypeFBMO ? 2 : 1
		, previewProps = {
			libraryId,
			creativeId,
			filename,
			// adTypeClass,
			socialValues: { ...flattenedSocialVals, adStudioIframe },
			isLoaded,
			adType,
			placementType: format,
			baseDomain: API_URL,
			aspectRatio,
			partner: selectedTab,
			placement,
			fbPreviewVersion,
			muted: isMuted,
		}
		, title = filename ?? "Loading"
		, previewURL = `${PREVIEW_2_URL}/creative/${creativeId}`
		, pacType = socialValues?.placementAssetCustomizationType
		, isTypeFacebook = fbTypes.includes(adType)
		, isTypeGoogle = googleTypes.includes(adType);

	function onReplay(){
		setReloadCount(reloadCount + 1);
	}

	if([SOCIAL_AD_TYPES.FACEBOOK_IMAGE,
		SOCIAL_AD_TYPES.FACEBOOK_VIDEO,
		SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL].includes(adType)){
		headerProps.icon = "fab fa-meta meta-logo";
	}

	if(selectedPAC) {
		if(adType === SOCIAL_AD_TYPES.FACEBOOK_IMAGE_MO) {
			previewProps = {
				...previewProps,
				socialValues: {
					...flattenedSocialVals,
					image: selectedPAC.image,
				},
				aspectRatio: selectedPAC.type.name.toLowerCase(),
			};
		}
		if(adType === SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO) {
			previewProps = {
				...previewProps,
				socialValues: {
					...flattenedSocialVals,
					video: selectedPAC.video,
				},
				aspectRatio: selectedPAC.type.name.toLowerCase(),
			};
		}
	}

	const platformClass = SOCIAL_PLATFORM_TYPES[adType] || "";

	const previewOpts = useSelector(facebookPreviewSelector(adType));
	const genericPlacementTypes = pacTypes.aspectRatios?.map?.(item => item.externalId) || [];
	const shouldUseRhombusPreview = genericPlacementTypes.includes(format);

	const isPacTypesLoading = (pacType !== "none") && !pacTypes.isLoaded;
	const $errorBanner = <ErrorBanner title="Error Fetching Creative" messages={[ftPreviewSrcError]} />;
	const isFbMOLoading = isTypeFBMO && shouldUseRhombusPreview && !placement;
	const isFTLoading = isTypeFacebook && !ftPreviewSrc && !ftPreviewSrcError && !shouldUseRhombusPreview && !isTypeFBMO;
	const isLoading = !isLoaded || isFTLoading || isPacTypesLoading || isFbMOLoading;

	useEffect(() => {
		// Default to first available aspect ratio for facebook pac
		if (isLoaded && isMetaDataLoaded && isTypeFacebook && pacType === "aspectRatio" && selectedTab === "generic") {
			const placementType = socialValues?.placements?.find(v => v.status === "active" && !!v?.type?.id)?.type;

			if (placementType?.externalId) {
				const newPlacement = previewOpts.find(v => v.value === placementType.externalId);

				// Prevent changing the default aspect ratio if no matched placement is found
				if (newPlacement) {
					dispatch(changePlacement(newPlacement));
				}
			}
		}
	}, [
		isLoaded,
		isMetaDataLoaded,
		isTypeFacebook,
		selectedTab,
		socialValues,
		previewOpts,
	]);

	function ftTypePreviewComponent() {
		let $component = null;

		if(isTypeFBMO) {
			$component = <SocialPreview autoPlay key={reloadCount} {...previewProps} />;
		} else {
			if(shouldUseRhombusPreview) {
				$component = <SocialPreview autoPlay key={reloadCount} {...previewProps} />;
			} else {
				$component = <iframe className="preview-iframe" src={ftPreviewSrc} />;
			}
		}

		return (
			<>
				{isTypeFBMO ? <FacebookMOSocialTabSelector adType={adType} /> : <FacebookSocialTabSelector adType={adType} />}
				<div className="preview-wrapper-div">{$component}</div>
				{/* generic preview ad_format doesnt exist API side and throws error*/}
				{isTypeFacebook && ftPreviewSrcError && !shouldUseRhombusPreview ? $errorBanner : ""}
			</>
		);
	}

	function GooglePreviewComponent() {
		return (
			<GooglePreview>
				<SocialPreview autoPlay key={reloadCount} {...previewProps} />
			</GooglePreview>
		);
	}

	let $previewComponent;

	if(isTypeFacebook) {
		$previewComponent = ftTypePreviewComponent();
	}else if(isTypeGoogle) {
		$previewComponent = GooglePreviewComponent();
	}else {
		$previewComponent  = <SocialPreview autoPlay key={reloadCount} {...previewProps} />;
	}

	function closeModal(){
		dispatch(resetPlacement());
		dispatch(reset(LABEL_MANAGEMENT)); /*
			We don't destroy the label management form on unmount, so we have to reset it manually
			when the blade closes.
		*/
		dispatch(push(returnPath));
	}

	function onToggleFullScreen(){
		setIsFullScreen(!isFullScreen);
	}

	function Footer(){
		return <Button theme="default" onClick={closeModal}>Close</Button>;
	}

	useEffect(()=>{
		if(isTypeFacebook && !shouldUseRhombusPreview && !isTypeFBMO) {
			// no need to dispatch to ITAPI for generic preview
			dispatch(loadFacebookPreview(libraryId, creativeId, dspConnectionId, format));
		}
	}, [libraryId, creativeId, dspConnectionId, format, isTypeFacebook, shouldUseRhombusPreview]);

	useEffect(() => {
		if(isTypeFBMO) {
			//set initial placement tab to facebook if adtype FBMO
			dispatch(changeTab(FACEBOOK_TAB));
		}
	}, [adType]);

	function Content(){
		return (
			<div className={classNames("main-content-container", `${(isTypeFacebook || isTypeGoogle) ? "ft-type-preview" : ""}`)}>
				<ProgressContainer
					className="preview-container"
					isLoading={isLoading}
				>
					{$previewComponent}
				</ProgressContainer>
				<LabelsPanel
					libraryId={libraryId}
					creativeId={creativeId}
				/>
				<CreativePreviewBladeToolbar
					onReplay={onReplay}
					setIsMuted={setIsMuted}
					isCarouselVideo={isCarouselVideo}
					muted={isMuted}
					previewURL={previewURL}
					adType={adType}
				/>
			</div>
		);
	}

	let modalProps = {
		title,
		type: "blade",
		className: `social-preview-blade expandable ${platformClass}`,
		setAppTitle:false,

		xClose: true,
		allowFullScreen: true,
		isFullScreen,
		onToggleFullScreen,

		bladeProps: {
			underlayStyle: {
				background: "none",
				pointerEvents: "none",
			},
			focusTrapPaused: true,
		},
		headerProps,
		renderFooter: Footer,
		renderContent: Content,
		onClose: closeModal,
	};

	return <Modal {...modalProps} ref={modalRef} />;
}

PreviewBlade.displayName = "PreviewBlade";
